import { Icon } from '@easy-expense/ui-shared-components';
import { Layout } from '@easy-expense/ui-web-core';
import React from 'react';
import {
  type ReactZoomPanPinchContentRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';

const CONTROL_BUTTON_STYLE = {
  backgroundColor: 'rgba(0,0,0,0.55)',
  borderRadius: 4,
  padding: 6,
} as const;

export const ZoomableImage: React.FC<{
  uri: string;
  onError?: () => void;
}> = ({ uri, onError }) => {
  const transformRef = React.useRef<ReactZoomPanPinchContentRef>(null);
  const [isPanning, setIsPanning] = React.useState(false);
  const [scale, setScale] = React.useState(1);
  const scaleRef = React.useRef(1);
  const mouseDownPosRef = React.useRef<{ x: number; y: number } | null>(null);

  let cursor = 'zoom-in';
  if (isPanning) {
    cursor = 'grabbing';
  } else if (scale > 1) {
    cursor = 'grab';
  }

  return (
    <Layout.Column
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        alignSelf: 'stretch',
      }}
    >
      <TransformWrapper
        ref={transformRef}
        minScale={1}
        maxScale={4}
        doubleClick={{ disabled: true }}
        limitToBounds
        onTransform={(_, state) => {
          scaleRef.current = state.scale;
          setScale(state.scale);
        }}
        onPanningStart={() => setIsPanning(true)}
        onPanningStop={() => setIsPanning(false)}
      >
        <TransformComponent
          wrapperStyle={{ width: '100%', height: '100%', cursor }}
          contentStyle={{ width: '100%', height: '100%' }}
          wrapperProps={{
            onMouseDown: (e) => {
              mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
            },
            onClick: (e) => {
              const startPos = mouseDownPosRef.current;
              if (startPos) {
                const dx = e.clientX - startPos.x;
                const dy = e.clientY - startPos.y;
                if (Math.sqrt(dx * dx + dy * dy) > 5) {
                  return;
                }
              }
              if (scaleRef.current > 1) {
                transformRef.current?.resetTransform();
              } else {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const mouseX = e.clientX - rect.left;
                const mouseY = e.clientY - rect.top;
                const newScale = 2;
                transformRef.current?.setTransform(
                  mouseX * (1 - newScale),
                  mouseY * (1 - newScale),
                  newScale,
                );
              }
            },
          }}
        >
          <img
            src={uri}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onError={onError}
          />
        </TransformComponent>
      </TransformWrapper>
      <Layout.Column
        style={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          gap: 4,
          zIndex: 10,
        }}
      >
        <Layout.PressableColumn
          center
          style={CONTROL_BUTTON_STYLE}
          onClick={() => transformRef.current?.zoomIn()}
        >
          <Icon name="add" size={16} color="white" />
        </Layout.PressableColumn>
        <Layout.PressableColumn
          center
          style={CONTROL_BUTTON_STYLE}
          onClick={() => transformRef.current?.zoomOut()}
        >
          <Icon name="remove" size={16} color="white" />
        </Layout.PressableColumn>
        <Layout.PressableColumn
          center
          style={CONTROL_BUTTON_STYLE}
          onClick={() => transformRef.current?.resetTransform()}
        >
          <Icon name="scan-outline" size={16} color="white" />
        </Layout.PressableColumn>
      </Layout.Column>
    </Layout.Column>
  );
};
